import React, { useState } from 'react';
import PropertySelectionTableRow from './PropertySelectionTableRow';
import { GutterCap, Header, IconChevron } from '@adserve/adserve-react-components';

export const cellStyle = (h = 30, w = 30, bg = 'FFFFFF', bd = '1px solid #D7D7D7') => {
    return {
        boxSizing: 'border-box',
        height: h,
        width: w,
        border: bd,
        backgroundColor: bg
    };
};

const PropertySelectionTable = ({
    activeProperties,
    newSelectedProperties,
    setNewSelectedProperties,
    setPropertyStatusMessage,
    checkedProperties,
    setCheckedProperties
}) => {
    const maxRows = 9;
    const rowHeight = 30;
    const [scrollPx, setScrollPx] = useState(0);
    const [highlightedRow, setHightlightedRow] = useState(-1);

    const padWithEmptyRows = () => {
        const retVal = [];
        for (let i = activeProperties.length; i < maxRows; i++) {
            retVal.push(
                <PropertySelectionTableRow
                    key={i}
                    disabled
                    rowHeight={rowHeight}
                    index={i}
                    scrollPx={scrollPx}
                    visibleRows={maxRows}
                    onScroll={setScrollPx}
                    scrollableLength={rowHeight * activeProperties.length}
                    selectedProperties={newSelectedProperties}
                />
            );
        }
        return retVal;
    };

    const propComparator = (propName) => (a, b) => a[propName] == b[propName] ? 0 : a[propName] < b[propName] ? -1 : 1;

    const propComparatorCostingType = (propName) => (a, b) =>
        a?.sandPPropertyCostingType[propName] == b?.sandPPropertyCostingType[propName]
            ? 0
            : a?.sandPPropertyCostingType[propName] < b?.sandPPropertyCostingType[propName]
                ? -1
                : 1;

    const propComparatorReverse = (propName) => (a, b) =>
        a[propName] == b[propName] ? 0 : a[propName] < b[propName] ? 1 : -1;

    const propComparatorCostingTypeReverse = (propName) => (a, b) =>
        a?.sandPPropertyCostingType[propName] == b?.sandPPropertyCostingType[propName]
            ? 0
            : a?.sandPPropertyCostingType[propName] < b?.sandPPropertyCostingType[propName]
                ? 1
                : -1;

    const sortByField = (field, direction) => {
        if (field === 'sandPPropertyCostingTypeName') {
            if (direction === 'desc') {
                activeProperties.sort(propComparatorCostingType('name'));
            } else {
                activeProperties.sort(propComparatorCostingTypeReverse('name'));
            }
        } else {
            if (direction === 'desc') {
                activeProperties.sort(propComparator(field));
            } else {
                activeProperties.sort(propComparatorReverse(field));
            }
        }
    };

    const [sortableCols, setSortableCols] = useState([
        {
            field: 'name',
            order: 'asc'
        },
        {
            field: 'linkedStationNames',
            order: 'asc'
        },
        {
            field: 'sandPPropertyCostingTypeName',
            order: 'asc'
        },
        {
            field: 'startDate',
            order: 'asc'
        },
        {
            field: 'endDate',
            order: 'asc'
        },
        {
            field: 'propertyTotalSpots',
            order: 'asc'
        },
        {
            field: 'propertyAvailableSpots',
            order: 'asc'
        },
        {
            field: 'propertyListPrice',
            order: 'asc'
        }
    ]);

    const handleSortClick = (field) => {
        const colsIndex = sortableCols?.findIndex((s) => s?.field === field);
        const updatedSortableCols = [...sortableCols];
        updatedSortableCols[colsIndex].order = updatedSortableCols[colsIndex].order === 'asc' ? 'desc' : 'asc';
        setSortableCols(updatedSortableCols);
        sortByField(field, updatedSortableCols[colsIndex].order);
    };

    return (
        <div
            id='PropertySelectionTable'
            style={{
                display: 'grid',
                gridTemplateColumns: 'max-content repeat(8, auto) 19px',
                boxSizing: 'border-box',
                overflowY: 'hidden',
                height: (maxRows + 1) * rowHeight
            }}
        >
            <Header id='selectHeader' rowHeight={rowHeight} scrollPx={scrollPx} style={{ borderWidth: 1 }} />
            <Header onClick={() => handleSortClick('name')} id='nameHeader' rowHeight={rowHeight} scrollPx={scrollPx}>
                <div style={{ position: 'absolute', marginLeft: 70 }}>
                    <IconChevron
                        width={11}
                        style={{
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            transform: sortableCols[0]?.order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}
                    />
                </div>
                {window.translate('Name')}
            </Header>
            <Header
                onClick={() => handleSortClick('linkedStationNames')}
                id='stationsHeader'
                rowHeight={rowHeight}
                scrollPx={scrollPx}
            >
                <div style={{ position: 'absolute', marginLeft: 80 }}>
                    <IconChevron
                        width={11}
                        style={{
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            transform: sortableCols[1]?.order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}
                    />
                </div>
                {window.translate('Stations')}
            </Header>
            <Header
                onClick={() => handleSortClick('sandPPropertyCostingTypeName')}
                id='typeHeader'
                rowHeight={rowHeight}
                scrollPx={scrollPx}
            >
                <div style={{ position: 'absolute', marginLeft: 110 }}>
                    <IconChevron
                        width={11}
                        style={{
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            transform: sortableCols[2]?.order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}
                    />
                </div>
                {window.translate('Property Type')}
            </Header>
            <Header
                onClick={() => handleSortClick('startDate')}
                id='startDateHeader'
                rowHeight={rowHeight}
                scrollPx={scrollPx}
            >
                <div style={{ position: 'absolute', marginLeft: 85 }}>
                    <IconChevron
                        width={11}
                        style={{
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            transform: sortableCols[3]?.order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}
                    />
                </div>
                {window.translate('Start Date')}
            </Header>
            <Header
                onClick={() => handleSortClick('endDate')}
                id='endDateHeader'
                rowHeight={rowHeight}
                scrollPx={scrollPx}
            >
                <div style={{ position: 'absolute', marginLeft: 80 }}>
                    <IconChevron
                        width={11}
                        style={{
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            transform: sortableCols[4]?.order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}
                    />
                </div>
                {window.translate('End Date')}
            </Header>
            <Header
                onClick={() => handleSortClick('propertyTotalSpots')}
                id='totalHeader'
                rowHeight={rowHeight}
                scrollPx={scrollPx}
            >
                <div style={{ position: 'absolute', marginLeft: 50 }}>
                    <IconChevron
                        width={11}
                        style={{
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            transform: sortableCols[5]?.order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}
                    />
                </div>
                {window.translate('Total')}
            </Header>
            <Header
                onClick={() => handleSortClick('propertyAvailableSpots')}
                id='availableHeader'
                rowHeight={rowHeight}
                scrollPx={scrollPx}
            >
                <div style={{ position: 'absolute', marginLeft: 75 }}>
                    <IconChevron
                        width={11}
                        style={{
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            transform: sortableCols[6]?.order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}
                    />
                </div>
                {window.translate('Available')}
            </Header>
            <GutterCap
                onClick={() => handleSortClick('propertyListPrice')}
                id='listPriceHeader'
                rowHeight={rowHeight}
                scrollPx={scrollPx}
            >
                <div style={{ position: 'absolute', marginLeft: 80 }}>
                    <IconChevron
                        width={11}
                        style={{
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            transform: sortableCols[7]?.order === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)'
                        }}
                    />
                </div>
                {window.translate('List Price')}
            </GutterCap>

            {activeProperties.map((ap, i) => (
                <PropertySelectionTableRow
                    key={i}
                    rowHeight={rowHeight}
                    visibleRows={maxRows}
                    activeProperty={ap}
                    index={i}
                    onScroll={setScrollPx}
                    setHighlightedRow={setHightlightedRow}
                    isHighlighted={i === highlightedRow}
                    scrollPx={scrollPx}
                    scrollableLength={rowHeight * activeProperties.length}
                    selectedProperties={newSelectedProperties}
                    setSelectedProperties={setNewSelectedProperties}
                    setPropertyStatusMessage={setPropertyStatusMessage}
                    checkedProperties={checkedProperties}
                    setCheckedProperties={setCheckedProperties}
                />
            ))}

            {padWithEmptyRows()}
        </div>
    );
};

export default PropertySelectionTable;
